import {
  useCreateAddress,
  useCreateOrganisation,
  useCreateSite,
  useUpdateOrganisation,
} from '@my/directory-api/hooks';
import {
  AddressInlineEditor,
  Button,
  ErrorBanner,
  FormSection,
  RegionalSettingsInlineEditor,
  SelectInput,
  TextInput,
  showToast,
} from '@my/ui';
import { useState } from 'react';
import {
  EMPTY_ADDRESS_FORM,
  toCreateAddressRequest,
  toCreateSiteRequest,
  type AddressForm,
} from './lib/organisationForm';
import {
  COUNTRY_OPTIONS,
  CURRENCY_OPTIONS,
  DATE_PATTERN_OPTIONS,
  LANGUAGE_OPTIONS,
  TIMEZONE_OPTIONS,
  TIME_PATTERN_OPTIONS,
} from './lib/selectOptions';

interface Regional {
  operationalTimeZone: string;
  languageCode: string;
  timePattern: string;
  datePattern: string;
}

/** What a brand-new organisation starts as before anyone touches it. */
const DEFAULT_REGIONAL: Regional = {
  operationalTimeZone: 'Europe/Dublin',
  languageCode: 'en',
  timePattern: '24h',
  datePattern: 'dd/MM/yy',
};

/**
 * Create an organisation.
 *
 * <p>
 * THE BASE CURRENCY IS HERE BECAUSE THIS IS THE ONLY PLACE IT CAN BE. It is
 * read-only on the organisation panel — changing it later reshuffles the
 * additional-currency list and every bank account denominated in it — so an
 * organisation created on the wrong currency cannot be corrected from My Hub at
 * all. Everything else on this form is editable afterwards; this one is not.
 * </p>
 *
 * <p>
 * Address and regional settings are here for a softer reason: they are what
 * somebody setting up a company already knows, and filling them in now saves a
 * second pass. Both remain editable on the panel.
 * </p>
 */
export function NewOrganisationPanel({
  defaults,
  onDone,
}: {
  /**
   * Seed values from an existing organisation in the account. A second company
   * under the same customer is almost always in the same country on the same
   * currency, and the server's IE/EUR fallback would be wrong for every
   * non-Irish account.
   */
  defaults?: { currencyIsoCode?: string; countryIsoCode?: string } & Partial<Regional>;
  onDone: () => void;
}) {
  const createOrganisation = useCreateOrganisation();
  const createAddress = useCreateAddress();
  const createSite = useCreateSite();
  const updateOrganisation = useUpdateOrganisation();

  const [name, setName] = useState('');
  const [invoicingName, setInvoicingName] = useState('');
  const [currencyIsoCode, setCurrencyIsoCode] = useState(defaults?.currencyIsoCode ?? 'EUR');
  const [regional, setRegional] = useState<Regional>({ ...DEFAULT_REGIONAL, ...defaults });
  const [address, setAddress] = useState<AddressForm>({
    ...EMPTY_ADDRESS_FORM,
    countryIsoCode: defaults?.countryIsoCode ?? EMPTY_ADDRESS_FORM.countryIsoCode,
  });
  const [error, setError] = useState<string | null>(null);

  const isSaving =
    createOrganisation.isPending ||
    createAddress.isPending ||
    createSite.isPending ||
    updateOrganisation.isPending;

  const canSubmit = name.trim().length > 0 && invoicingName.trim().length > 0 && !isSaving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);

    try {
      // The country follows the address, as it does on the panel and in TMS:
      // it drives the tax schedule, so it must not be a separate field that can
      // disagree with where the company actually is.
      const countryIsoCode = address.countryIsoCode.trim().toUpperCase() || 'IE';

      const organisationId = await createOrganisation.mutateAsync({
        name: name.trim(),
        invoicingName: invoicingName.trim(),
        currencyIsoCode,
        countryIsoCode,
        ...regional,
        // No apps: that would seed the CALLER a role, which a Stratum support
        // identity cannot have. Apps are enabled from Subscriptions, which
        // grants the role to the account's owners.
        appKeys: [],
      });

      // The address is a second step because POST /organisations has no field
      // for it — an Address belongs to an organisation, so it cannot exist
      // until the organisation does.
      if (address.fullAddress.trim()) {
        const addressId = await createAddress.mutateAsync(
          toCreateAddressRequest(organisationId, address),
        );
        // Every Address belongs to exactly one Site; without the pair the row
        // is one nothing else in the suite can point at.
        await createSite.mutateAsync(toCreateSiteRequest(organisationId, addressId));

        // Link it. A PUT is the only way to set mainOfficeAddressId, and it is
        // a whole-object replace — safe here only because the organisation was
        // created moments ago from these same values and has nothing else on it
        // yet.
        await updateOrganisation.mutateAsync({
          organisationId,
          name: name.trim(),
          invoicingName: invoicingName.trim(),
          taxNumber: null,
          registrationNumber: null,
          currencyIsoCode,
          countryIsoCode,
          ...regional,
          additionalCurrencyIsoCodes: null,
          extraFields: null,
          accountingSystem: 'None',
          vatRoundingMethod: 'Total',
          defaultPaymentTermDays: 30,
          defaultPaymentTermBasis: 'InvoiceDate',
          mainOfficeAddressId: addressId,
        });
      }

      showToast({ type: 'success', message: `${name.trim()} created.` });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the organisation.');
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      {error && <ErrorBanner title="Could not create the organisation" message={error} />}

      <FormSection
        title="New organisation"
        columns={3}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={onDone} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSaving ? 'Creating…' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="What this company is called day to day."
            required
          />

          <AddressInlineEditor
            label="Address"
            value={address}
            onChange={setAddress}
            countryOptions={COUNTRY_OPTIONS}
            enablePlacesSearch
            hasCodeField
          />
        </div>

        <div className="space-y-3 text-sm">
          <TextInput
            label="Invoicing Name"
            value={invoicingName}
            onChange={(e) => setInvoicingName(e.target.value)}
            helperText="The legal name that appears on documents."
            required
          />

          <SelectInput
            label="Base Currency"
            options={CURRENCY_OPTIONS}
            value={currencyIsoCode}
            onChange={(e) => setCurrencyIsoCode(e.target.value)}
            helperText="Set it now — it cannot be changed from My Hub afterwards."
          />
        </div>

        <div className="space-y-3 text-sm">
          <RegionalSettingsInlineEditor
            label="Regional Settings"
            value={regional}
            onChange={(next) => setRegional((r) => ({ ...r, ...next }))}
            timeZoneOptions={TIMEZONE_OPTIONS}
            languageOptions={LANGUAGE_OPTIONS}
            timePatternOptions={TIME_PATTERN_OPTIONS}
            datePatternOptions={DATE_PATTERN_OPTIONS}
          />
        </div>
      </FormSection>
    </form>
  );
}
